export const CONTROL_LANDING_PAGE_URL =
  "https://lp.homedesignguild.com/atlanta-interior-designers";
export const TEST_LANDING_PAGE_URL =
  "https://lp.homedesignguild.com/atlanta-interior-designers-339260";

const ATTRIBUTION_COOKIE_NAME = "hdg_attribution";
const ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

export type AttributionVariant = "control" | "test" | "unknown";
export type GraphqlAttributionVariant = "CONTROL" | "TEST" | "UNKNOWN";
export type RegistrationUserType = "Homeowner" | "Designer";

export interface AttributionCookieState {
  anonymousId: string;
  firstTouchVariant: AttributionVariant;
  firstTouchSourceLandingPage: string;
  firstTouchReferrerUrl?: string;
  firstTouchOccurredAt: string;
  lastTouchVariant: AttributionVariant;
  lastTouchSourceLandingPage: string;
  lastTouchReferrerUrl?: string;
  lastTouchOccurredAt: string;
}

const sanitizeIdempotencyPiece = (value?: string | null) =>
  (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9+._:-]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeUrl = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  try {
    return new URL(trimmed).toString().replace(/\/$/, "");
  } catch {
    return trimmed.replace(/\/$/, "");
  }
};

const resolveVariantFromLandingPage = (
  landingPage?: string
): AttributionVariant => {
  if (!landingPage) {
    return "unknown";
  }

  if (landingPage.includes("atlanta-interior-designers-339260")) {
    return "test";
  }

  if (landingPage.includes("atlanta-interior-designers")) {
    return "control";
  }

  return "unknown";
};

const resolveSourceLandingPage = (variant: AttributionVariant): string => {
  if (variant === "control") {
    return CONTROL_LANDING_PAGE_URL;
  }
  if (variant === "test") {
    return TEST_LANDING_PAGE_URL;
  }
  return "unknown";
};

export const parsePossiblyMalformedQuery = (urlOrQuery: string): URLSearchParams => {
  if (!urlOrQuery) {
    return new URLSearchParams();
  }

  const trimmed = urlOrQuery.trim();
  if (!trimmed) {
    return new URLSearchParams();
  }

  const queryStartIndex = trimmed.indexOf("?");
  const rawQuery = queryStartIndex >= 0 ? trimmed.slice(queryStartIndex + 1) : trimmed;
  const queryWithoutHash = rawQuery.split("#", 1)[0] ?? "";
  const normalizedQuery = queryWithoutHash
    .replace(/%3F/gi, "&")
    .replace(/\?/g, "&")
    .replace(/^&+|&+$/g, "");

  return new URLSearchParams(normalizedQuery);
};

export const normalizeVariant = (value?: string | null): AttributionVariant => {
  const normalized = (value ?? "").trim().toLowerCase();

  if (normalized === "control") {
    return "control";
  }
  if (normalized === "test") {
    return "test";
  }
  return "unknown";
};

export const parseUserType = (value?: string | null): RegistrationUserType | undefined => {
  const normalized = (value ?? "").trim().toLowerCase();
  if (normalized.includes("homeowner")) {
    return "Homeowner";
  }
  if (normalized.includes("designer")) {
    return "Designer";
  }
  return undefined;
};

export const parseAttributionFromUrl = (urlOrQuery: string) => {
  const queryParams = parsePossiblyMalformedQuery(urlOrQuery);
  const variantFromQuery = normalizeVariant(queryParams.get("variant"));
  const sourceLandingPageFromQuery = normalizeUrl(queryParams.get("sourceLandingPage"));
  const referrerFromQuery = normalizeUrl(queryParams.get("referrerUrl"));

  const landingPageVariant =
    variantFromQuery === "unknown"
      ? resolveVariantFromLandingPage(sourceLandingPageFromQuery)
      : variantFromQuery;

  const resolvedVariant = landingPageVariant;
  const resolvedSourceLandingPage =
    sourceLandingPageFromQuery || resolveSourceLandingPage(resolvedVariant);

  return {
    userType: parseUserType(queryParams.get("userType")),
    variant: resolvedVariant,
    sourceLandingPage: resolvedSourceLandingPage,
    referrerUrl: referrerFromQuery,
  };
};

const parseCookieJson = (raw: string): AttributionCookieState | null => {
  try {
    const parsed = JSON.parse(raw) as AttributionCookieState;
    if (
      !parsed ||
      !parsed.anonymousId ||
      !parsed.firstTouchOccurredAt ||
      !parsed.lastTouchOccurredAt
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const serializeCookieValue = (value: AttributionCookieState): string =>
  encodeURIComponent(JSON.stringify(value));

const getCookieValue = (cookieName: string): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const prefix = `${cookieName}=`;
  const cookieParts = document.cookie.split(";").map((part) => part.trim());
  const cookie = cookieParts.find((part) => part.startsWith(prefix));
  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.slice(prefix.length));
};

const setCookieValue = (cookieName: string, value: string, maxAgeSeconds: number) => {
  if (typeof document === "undefined") {
    return;
  }

  const secureFlag = typeof window !== "undefined" && window.location.protocol === "https:"
    ? "; Secure"
    : "";

  document.cookie = `${cookieName}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secureFlag}`;
};

const createAnonymousId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `anon-${Math.random().toString(36).slice(2, 12)}`;
};

export const readAttributionCookie = (): AttributionCookieState | null => {
  const cookieValue = getCookieValue(ATTRIBUTION_COOKIE_NAME);
  if (!cookieValue) {
    return null;
  }

  return parseCookieJson(cookieValue);
};

export const writeAttributionCookie = (state: AttributionCookieState) => {
  setCookieValue(
    ATTRIBUTION_COOKIE_NAME,
    serializeCookieValue(state),
    ATTRIBUTION_COOKIE_MAX_AGE_SECONDS
  );
};

export const captureAttributionFromLocation = (input?: {
  href?: string;
  referrerUrl?: string;
}) => {
  const href =
    input?.href ||
    (typeof window !== "undefined" ? window.location.href : "");
  const referrerUrl =
    normalizeUrl(input?.referrerUrl) ||
    (typeof document !== "undefined" ? normalizeUrl(document.referrer) : undefined);

  const parsed = parseAttributionFromUrl(href);
  const existing = readAttributionCookie();
  const now = new Date().toISOString();

  const currentTouchVariant = parsed.variant;
  const currentTouchLandingPage = parsed.sourceLandingPage;
  const currentTouchReferrer = parsed.referrerUrl || referrerUrl;

  const nextState: AttributionCookieState = existing
    ? {
        anonymousId: existing.anonymousId || createAnonymousId(),
        firstTouchVariant: existing.firstTouchVariant || currentTouchVariant,
        firstTouchSourceLandingPage:
          existing.firstTouchSourceLandingPage || currentTouchLandingPage,
        firstTouchReferrerUrl:
          existing.firstTouchReferrerUrl || currentTouchReferrer,
        firstTouchOccurredAt: existing.firstTouchOccurredAt || now,
        lastTouchVariant: currentTouchVariant,
        lastTouchSourceLandingPage: currentTouchLandingPage,
        lastTouchReferrerUrl: currentTouchReferrer,
        lastTouchOccurredAt: now,
      }
    : {
        anonymousId: createAnonymousId(),
        firstTouchVariant: currentTouchVariant,
        firstTouchSourceLandingPage: currentTouchLandingPage,
        firstTouchReferrerUrl: currentTouchReferrer,
        firstTouchOccurredAt: now,
        lastTouchVariant: currentTouchVariant,
        lastTouchSourceLandingPage: currentTouchLandingPage,
        lastTouchReferrerUrl: currentTouchReferrer,
        lastTouchOccurredAt: now,
      };

  writeAttributionCookie(nextState);

  return {
    cookie: nextState,
    parsed,
  };
};

export const toGraphqlAttributionVariant = (
  variant: AttributionVariant
): GraphqlAttributionVariant => {
  if (variant === "control") {
    return "CONTROL";
  }
  if (variant === "test") {
    return "TEST";
  }
  return "UNKNOWN";
};

export const getFirstTouchAttributionInput = (state: AttributionCookieState | null) => {
  if (!state) {
    return {
      variant: "UNKNOWN" as GraphqlAttributionVariant,
      sourceLandingPage: "unknown",
    };
  }

  return {
    anonymousId: state.anonymousId,
    variant: toGraphqlAttributionVariant(state.firstTouchVariant),
    sourceLandingPage: state.firstTouchSourceLandingPage,
    referrerUrl: state.firstTouchReferrerUrl,
    occurredAt: state.firstTouchOccurredAt,
  };
};

export const getLastTouchAttributionInput = (state: AttributionCookieState | null) => {
  if (!state) {
    return {
      variant: "UNKNOWN" as GraphqlAttributionVariant,
      sourceLandingPage: "unknown",
    };
  }

  return {
    anonymousId: state.anonymousId,
    variant: toGraphqlAttributionVariant(state.lastTouchVariant),
    sourceLandingPage: state.lastTouchSourceLandingPage,
    referrerUrl: state.lastTouchReferrerUrl,
    occurredAt: state.lastTouchOccurredAt,
  };
};

export const buildEventIdempotencyKey = (
  eventType: string,
  input: {
    userId?: string | null;
    anonymousId?: string | null;
    membershipType?: string | null;
    variant?: string | null;
    scope?: string | null;
  }
) => {
  const pieces = [
    sanitizeIdempotencyPiece(eventType),
    sanitizeIdempotencyPiece(input.userId) ||
      sanitizeIdempotencyPiece(input.anonymousId) ||
      "unknown",
    sanitizeIdempotencyPiece(input.membershipType) || "unknown",
    sanitizeIdempotencyPiece(input.variant) || "unknown",
    sanitizeIdempotencyPiece(input.scope) || "default",
  ].filter(Boolean);

  return pieces.join(":").slice(0, 200);
};

export const buildRegisterCtaUrl = (
  userType: RegistrationUserType,
  variant: Exclude<AttributionVariant, "unknown">
) => {
  const url = new URL("https://homedesignguild.com/register");
  url.searchParams.set("userType", userType);
  url.searchParams.set("variant", variant);
  return url.toString();
};
